// The computations behind level, sparkline, stats, state_timeline, heatmap and health (VisionPlatform
// widget-library-2 WLB-10 to WLB-15). The fixtures' expected texts come from the same rules, so keep this file in
// step with that spec.
import type { GaugeBand } from "./expected";
import type { HistorySeries, PointState, WidgetInput } from "./generated/widget-fixture";
import { key } from "./shared";
import type { Widget } from "./types";

const HOUR = 3_600_000;

/** [t, v] of a series: the value, or for avg buckets their average; null for a gap. */
export function seriesValues(h: HistorySeries | undefined): { t: number; v: number | null }[] {
  return (h?.points ?? []).flatMap((p) => {
    const t = typeof p[0] === "string" ? Date.parse(p[0]) : NaN;
    if (Number.isNaN(t)) return [];
    const raw = p.length >= 4 ? p[2] : p[1];
    return [{ t, v: typeof raw === "number" && Number.isFinite(raw) ? raw : null }];
  });
}

/** The change over a sparkline's range: the latest value minus the first bucket's average (WLB-11). */
export function rangeChange(h: HistorySeries | undefined, latest: number): number | undefined {
  const first = seriesValues(h).find((p) => p.v !== null)?.v;
  return first === undefined || first === null ? undefined : latest - first;
}

/** Whether a change rounds to zero at the shown decimals. */
export function roundsToZero(v: number, decimals: number): boolean {
  return Math.round(Math.abs(v) * 10 ** decimals) === 0;
}

export interface RangeStats {
  min: number;
  minAt: number;
  avg: number;
  max: number;
  maxAt: number;
}

/** Min (lowest bucket min), max (highest bucket max) with their bucket starts, and the mean of the averages (WLB-12). */
export function rangeStats(h: HistorySeries | undefined): RangeStats | undefined {
  let out: RangeStats | undefined;
  let sum = 0;
  let n = 0;
  for (const p of h?.points ?? []) {
    const t = typeof p[0] === "string" ? Date.parse(p[0]) : NaN;
    const [lo, mid, hi] = p.length >= 4 ? [p[1], p[2], p[3]] : [p[1], p[1], p[1]];
    if (Number.isNaN(t) || typeof mid !== "number") continue;
    const low = typeof lo === "number" ? lo : mid;
    const high = typeof hi === "number" ? hi : mid;
    sum += mid;
    n++;
    if (!out) out = { min: low, minAt: t, avg: 0, max: high, maxAt: t };
    if (low < out.min) Object.assign(out, { min: low, minAt: t });
    if (high > out.max) Object.assign(out, { max: high, maxAt: t });
  }
  return out && { ...out, avg: sum / n };
}

export interface Span {
  from: number;
  to: number;
  on: boolean;
}

/** Each value holds until the next one and the last until `end`; null values are gaps (WLB-13). */
export function onOffSpans(h: HistorySeries | undefined, end: number): Span[] {
  const pts = (h?.points ?? []).flatMap((p) => {
    const t = typeof p[0] === "string" ? Date.parse(p[0]) : NaN;
    const v = p[1];
    if (Number.isNaN(t)) return [];
    return [{ t, v: typeof v === "boolean" ? v : typeof v === "number" ? v !== 0 : null }];
  });
  const out: Span[] = [];
  pts.forEach((p, i) => {
    const to = pts[i + 1]?.t ?? end;
    if (p.v === null || to <= p.t) return;
    const last = out.at(-1);
    // A repeated value continues its span: buckets of the same state draw as one bar.
    if (last && last.on === p.v && last.to === p.t) last.to = to;
    else out.push({ from: p.t, to, on: p.v });
  });
  return out;
}

export function onTime(spans: Span[]): number {
  return spans.reduce((s, x) => s + (x.on ? x.to - x.from : 0), 0);
}

/** Changes from off to on inside the range; a range that starts on is not a start. */
export function starts(spans: Span[]): number {
  return spans.filter((s, i) => s.on && i > 0 && spans[i - 1]?.on === false && spans[i - 1]?.to === s.from).length;
}

/** "<m> min", "<h> h" or "<h> h <m> min", rounded to the minute, as keys of the shared strings. */
export function durationText(ms: number, t: (k: string, o?: Record<string, unknown>) => string): string {
  const m = Math.round(ms / 60_000);
  const h = Math.floor(m / 60);
  const rest = m % 60;
  if (h === 0) return t("duration.minutes", { count: rest });
  if (rest === 0) return t("duration.hours", { count: h });
  return t("duration.hoursMinutes", { hours: h, minutes: rest });
}

/** A time's local parts in a time zone (the organisation's; the browser's when none is given). */
export function localParts(ms: number, tz?: string) {
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: tz,
    hourCycle: "h23",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    weekday: "short",
  }).formatToParts(new Date(ms));
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? "";
  const weekday = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].indexOf(get("weekday")) + 1;
  return {
    day: `${get("year")}-${get("month")}-${get("day")}`,
    hour: Number(get("hour")),
    minute: Number(get("minute")),
    second: Number(get("second")),
    weekday,
    hhmm: `${get("hour")}:${get("minute")}`,
  };
}

/** Local midnight `daysBack` days before `now`'s day. */
export function localMidnight(now: number, tz: string | undefined, daysBack = 0): number {
  let t = now - daysBack * 86_400_000;
  // Twice, so a daylight saving change between the two days lands on midnight too.
  for (let i = 0; i < 2; i++) {
    const p = localParts(t, tz);
    t -= ((p.hour * 60 + p.minute) * 60 + p.second) * 1000 + (t % 1000);
    if (localParts(t, tz).hour !== 0) t += localParts(t, tz).hour === 23 ? HOUR : -HOUR;
  }
  return t;
}

export interface HeatRow {
  day: string;
  weekday: number;
  cells: (number | null)[];
}

/** Rows of local days from `from` to `to`, oldest first, each 24 hours of values (WLB-14). */
export function heatRows(h: HistorySeries | undefined, from: number, to: number, tz?: string): HeatRow[] {
  const rows = new Map<string, HeatRow>();
  for (let t = from; t < to; t += HOUR) {
    const p = localParts(t, tz);
    if (!rows.has(p.day)) rows.set(p.day, { day: p.day, weekday: p.weekday, cells: Array(24).fill(null) });
  }
  for (const { t, v } of seriesValues(h)) {
    const p = localParts(t, tz);
    const row = rows.get(p.day);
    if (row && v !== null) row.cells[p.hour] = v;
  }
  return [...rows.values()];
}

/** The step of the five-step ramp for a value; the middle step when every value is the same. */
export function heatStep(v: number, lo: number, hi: number): number {
  if (hi <= lo) return 2;
  return Math.min(4, Math.floor(((v - lo) / (hi - lo)) * 5));
}

/** 0 to 4 signal bars: from dBm when the value is negative, else from a percentage (WLB-15). */
export function signalBars(v: unknown, offline: boolean): number {
  if (offline || typeof v !== "number") return 0;
  if (v < 0) return v >= -70 ? 4 : v >= -85 ? 3 : v >= -100 ? 2 : 1;
  return Math.min(4, Math.max(1, Math.ceil(v / 25)));
}

/** Each bar's name: the reading's for one device, the device's for one reading of several, else both (WLB-10). */
export function barNames(widget: Widget, input: WidgetInput): string[] {
  const bs = (widget.bindings ?? []).filter((b) => b.datapoint);
  const devices = new Set(bs.map((b) => b.device));
  const keys = new Set(bs.map((b) => b.datapoint));
  return bs.map((b) => {
    const p = input.points?.[key(b.device, b.datapoint ?? "")]?.name ?? b.datapoint ?? "";
    const d = input.devices?.[b.device]?.name ?? "";
    if (b.label) return b.label;
    if (devices.size === 1) return p;
    if (keys.size === 1 && devices.size === bs.length) return d;
    return `${d} · ${p}`;
  });
}

/** The band a value falls in; past the last band, the last; without bands, none. */
export function bandOf(v: number, bands: GaugeBand[]): GaugeBand["level"] | undefined {
  if (!bands.length) return undefined;
  return (bands.find((b) => v <= b.to) ?? bands[bands.length - 1])?.level;
}

/** Min and max of a level bar: config, else the point's range, else 0 and 100. */
export function levelEnds(cfg: Record<string, unknown>, point: PointState | undefined): [number, number] {
  const min = typeof cfg.min === "number" ? cfg.min : (point?.range?.[0] ?? 0);
  const max = typeof cfg.max === "number" ? cfg.max : (point?.range?.[1] ?? 100);
  return [min, max];
}
